/**
 * @file package-suggestion.ts
 * @description Расчёт места (коробки) по вложенным товарам (phase19.12 шаг 5).
 *
 * Вес — сумма по позициям, коробка — «стопка»: основание по самой крупной
 * позиции, высота складывается. Это оценка для менеджера, а не точная
 * упаковка: она даёт понятный старт и честно показывает, каких данных в
 * карточках товаров не хватает.
 */

import type { OrderDeliveryLineDto } from "./api-orders";
import type { DeliveryPackageDraft } from "./delivery-quote";

export interface PackageSuggestion {
  weightG: number | null;
  lengthMm: number | null;
  widthMm: number | null;
  heightMm: number | null;
  /** Названия позиций без веса в карточке товара. */
  missingWeight: string[];
  /** Названия позиций без габаритов. */
  missingDims: string[];
}

export function suggestPackageDims(lines: OrderDeliveryLineDto[]): PackageSuggestion {
  const missingWeight: string[] = [];
  const missingDims: string[] = [];
  let weight = 0;
  let length = 0;
  let width = 0;
  let height = 0;

  for (const line of lines) {
    const qty = line.quantity > 0 ? line.quantity : 1;
    if (line.weightG === null || line.weightG === undefined) missingWeight.push(line.name);
    else weight += line.weightG * qty;

    const dims = [line.lengthMm, line.widthMm, line.heightMm];
    if (dims.some((d) => d === null || d === undefined)) {
      missingDims.push(line.name);
      continue;
    }
    length = Math.max(length, line.lengthMm ?? 0);
    width = Math.max(width, line.widthMm ?? 0);
    height += (line.heightMm ?? 0) * qty;
  }

  const dimsReady = lines.length > 0 && missingDims.length === 0;
  return {
    weightG: lines.length > 0 && missingWeight.length === 0 ? Math.round(weight) : null,
    lengthMm: dimsReady ? length : null,
    widthMm: dimsReady ? width : null,
    heightMm: dimsReady ? height : null,
    missingWeight,
    missingDims,
  };
}

/**
 * Расчёт по товарам → значения полей места. Заполняем только то, что реально
 * посчиталось: нет габаритов в товарах — поля габаритов не трогаем.
 */
export function suggestionToDraftPatch(
  suggestion: PackageSuggestion,
): Partial<DeliveryPackageDraft> {
  const patch: Partial<DeliveryPackageDraft> = { touched: false };
  if (suggestion.weightG !== null) patch.weightG = String(suggestion.weightG);
  if (
    suggestion.lengthMm !== null &&
    suggestion.widthMm !== null &&
    suggestion.heightMm !== null
  ) {
    patch.lengthMm = String(suggestion.lengthMm);
    patch.widthMm = String(suggestion.widthMm);
    patch.heightMm = String(suggestion.heightMm);
  }
  return patch;
}

/**
 * То же для автоподстановки: возвращает патч, только если он что-то меняет —
 * иначе эффект в карточке зациклился бы на собственном обновлении.
 */
export function autoFillPatch(
  suggestion: PackageSuggestion,
  draft: DeliveryPackageDraft,
): Partial<DeliveryPackageDraft> | null {
  const full = suggestionToDraftPatch(suggestion);
  const patch: Partial<DeliveryPackageDraft> = {};
  for (const field of ["weightG", "lengthMm", "widthMm", "heightMm"] as const) {
    const next = full[field];
    if (next !== undefined && next !== draft[field]) patch[field] = next;
  }
  return Object.keys(patch).length > 0 ? patch : null;
}

/** Есть ли что подставлять кнопкой «Рассчитать по товарам». */
export function canSuggestPackage(suggestion: PackageSuggestion): boolean {
  return suggestion.weightG !== null || suggestion.lengthMm !== null;
}
